import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/forms/icon.component';
import type { ManualBlock, ManualTone } from './manual.types';

/**
 * Render de los bloques del manual. Sin `dangerouslySetInnerHTML`: las marcas
 * inline se parsean a nodos de React.
 */

const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

/** Convierte **negritas**, `código` y [texto](/ruta) en nodos. */
export function inline(text: string): React.ReactNode[] {
  const parts = text.split(INLINE_RE).filter((p) => p !== '');
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="manual-code">
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      const label = link[1] ?? '';
      const href = link[2] ?? '#';
      if (href.startsWith('http')) {
        return (
          <a key={i} href={href} target="_blank" rel="noreferrer" className="link-primary">
            {label}
          </a>
        );
      }
      return (
        <Link key={i} to={href} className="link-primary">
          {label}
        </Link>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/** Texto plano de un bloque, para el buscador. */
export function blockText(b: ManualBlock): string {
  switch (b.kind) {
    case 'p':
    case 'h4':
      return b.text;
    case 'ul':
    case 'ol':
    case 'steps':
      return b.items.join(' ');
    case 'table':
      return [b.caption ?? '', ...b.head, ...b.rows.flat()].join(' ');
    case 'kv':
      return b.items.map((i) => `${i.k} ${i.v}`).join(' ');
    case 'note':
      return `${b.title ?? ''} ${b.text}`;
    case 'code':
      return `${b.label ?? ''} ${b.text}`;
    case 'formula':
      return `${b.label} ${b.expr} ${b.example ?? ''} ${b.note ?? ''}`;
    default:
      return '';
  }
}

const TONE_STYLE: Record<ManualTone, { cls: string; icon: string }> = {
  info: { cls: 'border-info bg-light-info', icon: 'ki-information-5' },
  warn: { cls: 'border-warning bg-light-warning', icon: 'ki-shield-cross' },
  danger: { cls: 'border-danger bg-light-danger', icon: 'ki-cross-circle' },
  success: { cls: 'border-success bg-light-success', icon: 'ki-shield-tick' }
};

export default function Block({ block }: { block: ManualBlock }) {
  switch (block.kind) {
    case 'p':
      return <p className="fs-6 text-gray-800 lh-lg mb-4">{inline(block.text)}</p>;

    case 'h4':
      return <h4 className="fw-bold fs-5 text-gray-900 mt-8 mb-3">{inline(block.text)}</h4>;

    case 'ul':
      return (
        <ul className="fs-6 text-gray-800 lh-lg mb-4 ps-6">
          {block.items.map((it, i) => (
            <li key={i} className="mb-2">
              {inline(it)}
            </li>
          ))}
        </ul>
      );

    case 'ol':
      return (
        <ol className="fs-6 text-gray-800 lh-lg mb-4 ps-6">
          {block.items.map((it, i) => (
            <li key={i} className="mb-2">
              {inline(it)}
            </li>
          ))}
        </ol>
      );

    case 'steps':
      return (
        <div className="mb-6">
          {block.items.map((it, i) => (
            <div key={i} className="d-flex align-items-start mb-4">
              <span className="badge badge-circle badge-primary fw-bold me-4 mt-1 flex-shrink-0">
                {i + 1}
              </span>
              <div className="fs-6 text-gray-800 lh-lg">{inline(it)}</div>
            </div>
          ))}
        </div>
      );

    case 'table':
      return (
        <div className="mb-6">
          <div className="table-responsive">
            <table className="table table-row-bordered table-row-gray-200 align-middle fs-7 mb-0">
              <thead>
                <tr className="fw-bold text-muted text-uppercase fs-8">
                  {block.head.map((h, i) => (
                    <th key={i} className={`text-${block.align?.[i] ?? 'start'} py-3`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((c, j) => (
                      <td key={j} className={`text-${block.align?.[j] ?? 'start'} py-3`}>
                        {inline(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.caption && <div className="text-muted fs-8 mt-2">{inline(block.caption)}</div>}
        </div>
      );

    case 'kv':
      return (
        <div className="mb-6">
          {block.items.map((it, i) => (
            <div
              key={i}
              className="d-flex flex-column flex-md-row py-3 border-bottom border-gray-200"
            >
              <div className="fw-bold text-gray-900 fs-7 mb-1 mb-md-0" style={{ minWidth: 220 }}>
                {inline(it.k)}
              </div>
              <div className="fs-7 text-gray-700 lh-lg">{inline(it.v)}</div>
            </div>
          ))}
        </div>
      );

    case 'note': {
      const tone = TONE_STYLE[block.tone];
      return (
        <div className={`alert d-flex align-items-start p-5 rounded border ${tone.cls} mb-6`}>
          <Icon icon={tone.icon} size="fs-2hx" className="me-4 flex-shrink-0" />
          <div className="d-flex flex-column">
            {block.title && <span className="fw-bold fs-6 mb-1">{block.title}</span>}
            <span className="fs-7 lh-lg">{inline(block.text)}</span>
          </div>
        </div>
      );
    }

    case 'code':
      return (
        <div className="mb-6">
          {block.label && <div className="text-muted fs-8 mb-1">{block.label}</div>}
          <pre className="manual-pre rounded p-4 mb-0">
            <code>{block.text}</code>
          </pre>
        </div>
      );

    case 'formula':
      return (
        <div className="card bg-light-primary border border-primary border-dashed rounded-3 mb-6">
          <div className="card-body p-5">
            <div className="fw-bold text-primary fs-7 text-uppercase mb-2">{block.label}</div>
            <pre className="manual-pre manual-pre--formula rounded p-3 mb-0">
              <code>{block.expr}</code>
            </pre>
            {block.example && (
              <div className="fs-7 text-gray-800 mt-3">
                <span className="fw-semibold">Ejemplo: </span>
                {inline(block.example)}
              </div>
            )}
            {block.note && <div className="fs-8 text-muted mt-2">{inline(block.note)}</div>}
          </div>
        </div>
      );

    default:
      return null;
  }
}
